import { Box, Typography } from '@mui/material';
import { AppConfig } from 'AppConfig';
import Carousel from 'common/bstCarousel';
import * as Constant from 'common/Constant';
import * as Actions from 'features/frontoffice/customReport/actions/action';
import * as UserTypes from 'features/user/userTypes';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { store } from 'store/store';
import ReportFilters, {TeamSubfolderFilters, PersonalSubfolderFilters} from './reportFilters';
import * as Action from '../favoriteReport/actions/action';
import NoAvailableReports from 'features/frontoffice/component/noAvailableReports';
import { sortReports } from 'utils/reportSort';
import ReportSectionLoading from '../component/reportSectionLoading';
import { ADHOC_REPORT_CATEGORIES } from 'common/Constant';

const reportTags = ADHOC_REPORT_CATEGORIES;

const getReportTagName = (report) => {
  const tag = report?.reportTag;
  if (typeof tag === 'string') {
    return tag;
  }
  return typeof tag === 'object' ? tag?.name : null;
};

const normalizeTag = (value) => {
  return String(value ?? '').trim().toLowerCase();
};

const tagAdHocReports = (reports) =>
  (Array.isArray(reports) ? reports : []).map((r) => ({
    ...r,
    sourceType: 'adhoc'
  }));

export const filterBySelectedSubfolders = (items, rootName, selectedSubfolders) => {
  if (!Array.isArray(selectedSubfolders) || selectedSubfolders.length === 0) {
    return items;
  }
  return items.filter((item) => {
    const reportTag = normalizeTag(getReportTagName(item));
    const isRootReport = !reportTag || reportTag === rootName;
    return (
      (isRootReport && selectedSubfolders.some((subfolder) => subfolder?.code === 'uncategorized')) ||
      selectedSubfolders.some((subfolder) => subfolder?.code === reportTag)
    );
  });
};

const AdHocReports = ({ selectedFilterTags: selectedFilterTagsProp = [], isMobile = false, isTablet = false, searchTerm = '', sortBy, viewMode = 'grid' }) => {
  const adHocPublic = useSelector((state) => state.customReport.adHocPublic);
  const adHocPersonal = useSelector((state) => state.customReport.adHocPersonal);
  const adHocLoading = useSelector((state) => state.customReport.adHocLoading);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const currentMenu = useSelector((state) => state.favoriteReport.currentMenu);

  const sortKey = typeof sortBy === 'string' ? sortBy : sortBy?.value;

  const allSortedReports = useMemo(() => {
    const teamReports = Array.isArray(adHocPublic) ? adHocPublic : [];
    const merged = [
      ...tagAdHocReports(teamReports),
      ...tagAdHocReports(adHocPersonal)
    ];
    const filteredLegacies =
      sortKey === 'recently_saved'
        ? merged.filter((item) => item.favorited === true)
        : merged;
    return sortReports(filteredLegacies, sortKey);
}, [adHocPublic, adHocPersonal, sortKey]);


  const [data, setData] = useState(allSortedReports);
  const [searchKey, setSearchKey] = useState('');
  const [selectedFilterTags, setSelectedFilterTags] = useState([]);
  const [selectedTeamSubfolders, setSelectedTeamSubfolders] = useState([]);
  const [selectedPersonalSubfolders, setSelectedPersonalSubfolders] = useState([]);
  const selectedMobileFilterTags = Array.isArray(selectedFilterTagsProp) ? selectedFilterTagsProp : [];

  const searchedAdHocReports = useMemo(() => {
    const list = Array.isArray(allSortedReports) ? allSortedReports : [];
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return list;
    return list.filter((item) => {
      const title = (item?.displayText || '').toLowerCase();
      const desc = (item?.description || '').toLowerCase();
      return title.includes(term) || desc.includes(term);
    });
  }, [allSortedReports, searchTerm]);

  useEffect(() => {
    store.dispatch({ type: UserTypes.APP_SWITCHED, payload: AppConfig.HEADER_MENU_TYPE.FO });
    store.dispatch(Actions.fetchAdHocReportsForCustom());
  }, [currentTenantCd]);

  useEffect(() => {
    store.dispatch(Action.setCurrentMenuItem({ name: Constant.ALL }));
  }, []);

  const searchKeyLC = (searchKey || '').toLowerCase();
  const searchFilter = (value) => {
    if (Array.isArray(value)) {
      const newData = _.filter(value, (o) => {
        const title = (o?.displayText || '').toLowerCase();
        const desc = (o?.description || '').toLowerCase();
        return title.includes(searchKeyLC) || desc.includes(searchKeyLC);
      });
      setData(sortReports(newData, sortKey));
    }
  };

  const filter = (value) => {
    if (!Array.isArray(allSortedReports)) return;
    if (value === Constant.ALL) {
      searchFilter(allSortedReports);
      return;
    }
    const newData = _.filter(allSortedReports, (o) =>
      o.reportTag?.name ? o.reportTag?.name?.toLowerCase().includes(value?.toLowerCase()) : false
    );
    searchFilter(newData);
  };

  useEffect(() => {
    filter(currentMenu?.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyLC]);

  useEffect(() => {
    if (!searchTerm) {
      setSearchKey('');
    }
  }, [currentMenu, searchTerm]);

  const handleSelectionChange = useCallback((selectedTags = []) => {
    setSelectedFilterTags(selectedTags || []);
  }, []);

  useEffect(() => {
    const isSelected = (name) =>
      selectedFilterTags.some((tag) => normalizeTag(tag?.name) === name);
    if (!isSelected('team')) setSelectedTeamSubfolders([]);
    if (!isSelected('personal')) setSelectedPersonalSubfolders([]);
  }, [selectedFilterTags]);

  useEffect(() => {
    setSearchKey(searchTerm || '');
  }, [searchTerm]);

  const itemsForTag = (tag) => {
    if (!Array.isArray(searchedAdHocReports)) {
      return [];
    }

    let items = searchedAdHocReports.filter((o) => o?.category === tag?.code);

    const tagName = normalizeTag(tag?.name);
    if (tagName === 'team') {
      items = filterBySelectedSubfolders(items, 'team', selectedTeamSubfolders);
    } else if (tagName === 'personal') {
      items = filterBySelectedSubfolders(items, 'personal', selectedPersonalSubfolders);
    }

    return items;
  };

  const renderSubfolderFilters = (tag) => {
    const name = normalizeTag(tag?.name);
    if (name === 'team') {
      return (
        <TeamSubfolderFilters
          reports={adHocPublic}
          selectedSubfolders={selectedTeamSubfolders}
          onChange={setSelectedTeamSubfolders}
          filter={filter}
        />
      );
    }
    if (name === 'personal') {
      return (
        <PersonalSubfolderFilters
          reports={adHocPersonal}
          selectedSubfolders={selectedPersonalSubfolders}
          onChange={setSelectedPersonalSubfolders}
          filter={filter}
        />
      );
    }
    return null;
  };

    const renderTagSections = (tagsSelected) => {
    const isAll = !Array.isArray(tagsSelected) || tagsSelected.length === 0;

    if (isAll && (!Array.isArray(searchedAdHocReports) || searchedAdHocReports.length === 0)) {
      return <NoAvailableReports />;
    }

    if (isAll) {
      return reportTags.map((tag) => {
        const items = itemsForTag(tag);
        const isTeam = normalizeTag(tag?.name) === 'team';
        const isPersonal = normalizeTag(tag?.name) === 'personal';
        const hasSelectedTeamSubFolder = Array.isArray(selectedTeamSubfolders) &&
          selectedTeamSubfolders.length > 0;
        const hasSelectedPersonalSubFolder = Array.isArray(selectedPersonalSubfolders) &&
          selectedPersonalSubfolders.length > 0;

        if (
          !items.length &&
          !(isTeam && hasSelectedTeamSubFolder) &&
          !(isPersonal && hasSelectedPersonalSubFolder)
        ) {
          return null;
        }
        return (
          <Box key={tag.code} sx={{ mb: '24px', mt: '24px' }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Rubik',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '24px',
                color: '#000000',
                mb: '24px',
              }}>
              {tag?.name} ({items.length})
            </Typography>
            {renderSubfolderFilters(tag)}
            {items.length ? (
              <Carousel items={items} type={AppConfig.AD_HOC_CAROUSEL} isTablet={isTablet} viewMode={viewMode} />
              ) : ( <NoAvailableReports />

            )}

          </Box>
        );
      });
    }

    return tagsSelected.map((tag) => {
      const items = itemsForTag(tag);
      return (
        <Box key={tag.code} sx={{ mb: '24px', mt: '24px' }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Rubik',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '24px',
              color: '#000000',
              mb: '24px'
            }}>
            {tag?.name}
            {tag?.code ? ` (${items.length})` : ''}
          </Typography>
          {renderSubfolderFilters(tag)}
          {items.length ? <Carousel items={items} type={AppConfig.AD_HOC_CAROUSEL} isTablet={isTablet} viewMode={viewMode} /> : <NoAvailableReports />}
        </Box>
      );
    });
  };

  if (isMobile) {
    if (adHocLoading) return <ReportSectionLoading count={6} variant="grid" />;
    return <>{renderTagSections(selectedMobileFilterTags)}</>;    
  }
 
  return (
    <>
      {adHocLoading ? (
        <ReportSectionLoading count={10} variant="grid" />
      ) :
      !Array.isArray(allSortedReports) || !allSortedReports.length ? (
        <NoAvailableReports />
      ) : (
        <>
          {searchedAdHocReports.length > 0 && <Box sx={{ mb: 3 }}>
            <ReportFilters setData={setData} filter={filter} onSelectionChange={handleSelectionChange} />
          </Box>}
          <Box sx={{display: 'flex', flexDirection: 'column' }}>
            {renderTagSections(selectedFilterTags)}
          </Box>
        </>
      )}
    </>
  );
};

export default AdHocReports;
